import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useAddToCart } from '@corratech/pylot-cart-manager'
import { useRemoveProductsFromWishlist } from './use-remove-products-from-wishlist'
import { CartItemInput } from '@pylot-data/pylotschema'

export type RemoveProductFromWishlistArgs = {
    wishlistId: string
    wishlistItemsIds: Array<{ id: string; productId: string }>
}

export interface UseAddToCartFromWishlistInterface {
    isLoading: boolean
    addToCartFromWishlist: (
        formInput: Array<CartItemInput>,
        removeInput: RemoveProductFromWishlistArgs
    ) => void
}

export const useAddToCartFromWishlist = (): UseAddToCartFromWishlistInterface => {
    const { t } = useTranslation(['wishlist'])
    const [isLoading, setIsLoading] = useState(false)
    const { openSidebar, openCartToast, openToast } = useUI()
    const { addToCart } = useAddToCart()
    const { removeProductsFromWishlist } = useRemoveProductsFromWishlist()

    const addToCartFromWishlist = async (
        formInput: Array<CartItemInput>,
        removeInput: RemoveProductFromWishlistArgs
    ) => {
        setIsLoading(true)
        const response = await addToCart(formInput)

        if (response?.errors?.length) {
            openToast(response?.errors?.[0]?.message, ToastType.Danger)
            setIsLoading(false)
            return false
        }

        const items = response?.data?.cart?.items
        const userErrors = response?.user_errors
        const added = removeInput?.wishlistItemsIds?.filter((ids) => {
            return items?.find(
                (item) => ids?.productId == String(item?.product?.id)
            )
        })

        //Handle user error if no items added without opening sidebar
        if (userErrors?.length && !added.length) {
            openToast(userErrors[0]?.message!, ToastType.Danger)
        }

        //handle success and user errors
        if (added.length) {
            openSidebar()
            if (userErrors?.length) {
                openCartToast(userErrors[0]?.message!, ToastType.Danger)
            } else {
                openCartToast(
                    t('wishlist|{{ product }} has been added to cart', {
                        product: formInput?.length > 1 ? 'Products' : 'Product'
                    }),
                    ToastType.Success
                )
            }

            //consider remove items from wishlist after adding
            const removeIds = added.map((removeId) => String(removeId?.id))
            await removeProductsFromWishlist({
                ...removeInput,
                wishlistItemsIds: removeIds
            })
        }

        setIsLoading(false)
    }

    return {
        isLoading,
        addToCartFromWishlist
    }
}
